import { login, register, AuthError } from "@/auth/api";

export interface AuthResult {
  token: string;
  userId: string;
  displayName: string;
}

/**
 * Shows a DOM overlay with login/signup forms on top of the game canvas.
 * Returns a promise that resolves with the JWT token once auth succeeds.
 */
export function showAuthOverlay(): Promise<AuthResult> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.id = "auth-overlay";
    overlay.innerHTML = `
      <style>
        #auth-overlay {
          position: fixed; inset: 0; z-index: 9999;
          display: flex; align-items: center; justify-content: center;
          background: rgba(11, 13, 18, 0.95);
          font-family: system-ui, -apple-system, sans-serif;
          color: #e0ddd5;
        }
        #auth-box {
          background: #1a1d24; border: 1px solid #2a2d34; border-radius: 12px;
          padding: 32px; width: 340px; max-width: 90vw;
        }
        #auth-box h2 { margin: 0 0 20px; font-size: 20px; text-align: center; color: #c8b88a; }
        #auth-box input {
          display: block; width: 100%; padding: 10px 12px; margin-bottom: 12px;
          background: #0e1116; border: 1px solid #2a2d34; border-radius: 6px;
          color: #e0ddd5; font-size: 14px; box-sizing: border-box;
        }
        #auth-box input:focus { outline: none; border-color: #c8b88a; }
        #auth-box input::placeholder { color: #555; }
        #auth-btn {
          display: block; width: 100%; padding: 10px; margin-top: 4px;
          background: #c8b88a; color: #0e1116; border: none; border-radius: 6px;
          font-size: 14px; font-weight: 600; cursor: pointer;
        }
        #auth-btn:hover { background: #d4c89a; }
        #auth-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        #auth-toggle {
          display: block; margin-top: 16px; text-align: center;
          color: #888; font-size: 13px; cursor: pointer; background: none; border: none;
        }
        #auth-toggle:hover { color: #c8b88a; }
        #auth-error {
          color: #e8544e; font-size: 13px; margin-bottom: 12px; min-height: 18px;
        }
      </style>
      <div id="auth-box">
        <h2 id="auth-title">Log In</h2>
        <div id="auth-error"></div>
        <input id="auth-email" type="email" placeholder="Email" autocomplete="email" />
        <input id="auth-password" type="password" placeholder="Password" autocomplete="current-password" />
        <div id="signup-fields" style="display:none">
          <input id="auth-display" type="text" placeholder="Display Name" autocomplete="name" />
        </div>
        <button id="auth-btn">Log In</button>
        <button id="auth-toggle">Don't have an account? Sign up</button>
      </div>
    `;

    document.body.appendChild(overlay);

    const title = overlay.querySelector("#auth-title") as HTMLHeadingElement;
    const emailInput = overlay.querySelector("#auth-email") as HTMLInputElement;
    const passwordInput = overlay.querySelector("#auth-password") as HTMLInputElement;
    const displayInput = overlay.querySelector("#auth-display") as HTMLInputElement;
    const signupFields = overlay.querySelector("#signup-fields") as HTMLDivElement;
    const btn = overlay.querySelector("#auth-btn") as HTMLButtonElement;
    const toggle = overlay.querySelector("#auth-toggle") as HTMLButtonElement;
    const errorEl = overlay.querySelector("#auth-error") as HTMLDivElement;

    let isSignup = false;

    toggle.addEventListener("click", () => {
      isSignup = !isSignup;
      title.textContent = isSignup ? "Sign Up" : "Log In";
      btn.textContent = isSignup ? "Create Account" : "Log In";
      toggle.textContent = isSignup
        ? "Already have an account? Log in"
        : "Don't have an account? Sign up";
      signupFields.style.display = isSignup ? "block" : "none";
      passwordInput.autocomplete = isSignup ? "new-password" : "current-password";
      errorEl.textContent = "";
    });

    const submit = async () => {
      errorEl.textContent = "";
      btn.disabled = true;

      try {
        const email = emailInput.value.trim();
        const password = passwordInput.value;

        if (!email || !password) {
          errorEl.textContent = "Email and password are required.";
          return;
        }

        let result;
        if (isSignup) {
          const displayName = displayInput.value.trim();
          if (!displayName) {
            errorEl.textContent = "All fields are required.";
            return;
          }
          result = await register(email, password, displayName);
        } else {
          result = await login(email, password);
        }

        overlay.remove();
        resolve({ token: result.token, userId: result.user.id, displayName: result.user.display_name });
      } catch (err) {
        if (err instanceof AuthError) {
          if (err.detail.details) {
            const msgs = Object.entries(err.detail.details)
              .map(([field, errs]) => `${field}: ${(errs as string[]).join(", ")}`)
              .join("; ");
            errorEl.textContent = msgs;
          } else {
            errorEl.textContent = err.detail.message ?? err.detail.code;
          }
        } else {
          errorEl.textContent = "Network error — please try again.";
        }
      } finally {
        btn.disabled = false;
      }
    };

    btn.addEventListener("click", submit);
    overlay.addEventListener("keydown", (e) => {
      if (e.key === "Enter") submit();
    });

    emailInput.focus();
  });
}
